import React, { useState, useEffect, useRef } from 'react';
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import FrequencySlider, { getFrequencyDescription, getBrainwaveCategory, getBrainwaveCategoryColor } from './FrequencySlider';
import { toast } from 'sonner';
import { Settings, Lock, LockOpen, AudioLines, Play, Pause, Volume2, Eye } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { SessionStep } from './AdvancedSession';
import { AmbienceSound } from './AmbienceMixer';

interface CustomSessionProps {
  onCreateSession: (frequency: number, duration: number, title: string, advancedSettings?: AdvancedSettings) => void;
  onCreateProgressiveSession?: (
    startFreq: number, 
    endFreq: number, 
    duration: number, 
    transitionTime: number
  ) => void;
  onCreateAdvancedSession?: (steps: SessionStep[], ambientSounds?: AmbienceSound[]) => void;
}

export interface AdvancedSettings {
  leftFrequency: number;
  rightFrequency: number;
  carrierFrequency: number;
  leftWaveform?: string;
  rightWaveform?: string;
}

type Waveform = 'sine' | 'square' | 'triangle' | 'sawtooth';

const CARRIER_PRESETS = [
  { value: 100, label: '100 Hz', displayLabel: 'Low', description: 'subtle, gentle' },
  { value: 200, label: '200 Hz', displayLabel: 'Medium', description: 'balanced' },
  { value: 300, label: '300 Hz', displayLabel: 'Higher', description: 'more audible' },
  { value: 400, label: '400 Hz', displayLabel: 'High', description: 'pronounced' },
];

const WAVEFORM_OPTIONS: Waveform[] = ['sine', 'square', 'triangle', 'sawtooth'];

const PREVIEW_DURATION = 15; // seconds
const PREVIEW_FADEOUT_TIME = 2; // seconds for fade-out

const CustomSession: React.FC<CustomSessionProps> = ({ 
  onCreateSession,
  onCreateProgressiveSession,
  onCreateAdvancedSession 
}) => {
  const [frequency, setFrequency] = useState(10);
  const [duration, setDuration] = useState(15);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  const [carrierFrequency, setCarrierFrequency] = useState(200);
  const [leftFrequency, setLeftFrequency] = useState(200);
  const [rightFrequency, setRightFrequency] = useState(210);
  const [lockChannels, setLockChannels] = useState(true);
  
  const [leftWaveform, setLeftWaveform] = useState<Waveform>('sine');
  const [rightWaveform, setRightWaveform] = useState<Waveform>('sine');
  
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewTimeLeft, setPreviewTimeLeft] = useState(0);
  const previewTimerRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioNodesRef = useRef<{
    leftOsc?: OscillatorNode;
    rightOsc?: OscillatorNode;
    masterGain?: GainNode;
    cleanup?: () => void;
  }>({});
  
  const [beatFrequency, setBeatFrequency] = useState(Math.abs(rightFrequency - leftFrequency));
  const [brainwaveCategory, setBrainwaveCategory] = useState(getBrainwaveCategory(beatFrequency));
  const [categoryColor, setCategoryColor] = useState(getBrainwaveCategoryColor(brainwaveCategory));
  
  // Reset appropriate values when toggling between basic and advanced modes
  useEffect(() => {
    if (showAdvanced) {
      // When switching to advanced mode, ensure frequencies are in sync with the basic frequency
      updateChannelsFromBeat(frequency);
    } else {
      // When switching to basic mode, carrier and channels aren't used directly
      // but we should reset them for next time advanced mode is used
      setCarrierFrequency(200);
      setLeftFrequency(200);
      setRightFrequency(200 + frequency);
      setLeftWaveform('sine');
      setRightWaveform('sine');
    }
  }, [showAdvanced, frequency]);
  
  useEffect(() => {
    const newBeatFrequency = Math.abs(rightFrequency - leftFrequency);
    setBeatFrequency(newBeatFrequency);
    
    const newCategory = getBrainwaveCategory(newBeatFrequency);
    setBrainwaveCategory(newCategory);
    setCategoryColor(getBrainwaveCategoryColor(newCategory));
  }, [leftFrequency, rightFrequency]);
  
  useEffect(() => {
    return () => {
      if (previewTimerRef.current) {
        clearInterval(previewTimerRef.current);
      }
      cleanupAudio();
    };
  }, []);
  
  const generatePreview = () => {
    if (isPreviewing) {
      cleanupAudio();
      return;
    }
    
    setIsPreviewing(true);
    setPreviewTimeLeft(PREVIEW_DURATION);
    
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const audioContext = new AudioContextClass();
    audioContextRef.current = audioContext;
    
    const leftOsc = audioContext.createOscillator();
    const rightOsc = audioContext.createOscillator();
    
    const leftGain = audioContext.createGain();
    const rightGain = audioContext.createGain();
    const masterGain = audioContext.createGain();
    
    if (showAdvanced) {
      leftOsc.frequency.value = leftFrequency;
      rightOsc.frequency.value = rightFrequency;
    } else {
      leftOsc.frequency.value = carrierFrequency;
      rightOsc.frequency.value = carrierFrequency + frequency;
    }
    
    leftOsc.type = leftWaveform as OscillatorType;
    rightOsc.type = rightWaveform as OscillatorType;
    
    const leftPanner = audioContext.createStereoPanner();
    const rightPanner = audioContext.createStereoPanner();
    leftPanner.pan.value = -1;
    rightPanner.pan.value = 1;
    
    leftOsc.connect(leftGain);
    rightOsc.connect(rightGain);
    leftGain.connect(leftPanner);
    rightGain.connect(rightPanner);
    leftPanner.connect(masterGain);
    rightPanner.connect(masterGain);
    masterGain.connect(audioContext.destination);
    
    leftGain.gain.value = 0.3;
    rightGain.gain.value = 0.3;
    masterGain.gain.value = 0.2;
    
    audioNodesRef.current = {
      leftOsc,
      rightOsc,
      masterGain
    };
    
    leftOsc.start();
    rightOsc.start();
    
    masterGain.gain.linearRampToValueAtTime(0.5, audioContext.currentTime + 0.5);
    
    toast.info(`Previewing ${showAdvanced ? beatFrequency : frequency}Hz for ${PREVIEW_DURATION} seconds`, {
      description: "Listening to a brief preview of this session"
    });
    
    previewTimerRef.current = window.setInterval(() => {
      setPreviewTimeLeft(prev => {
        const newTime = prev - 1;
        
        if (newTime <= PREVIEW_FADEOUT_TIME && audioNodesRef.current.masterGain) {
          const fadeOutStartTime = audioContext.currentTime;
          audioNodesRef.current.masterGain.gain.linearRampToValueAtTime(0, fadeOutStartTime + PREVIEW_FADEOUT_TIME);
        }
        
        if (newTime <= 0) {
          cleanupAudio();
          return 0;
        }
        
        return newTime;
      });
    }, 1000) as unknown as number;
  };
  
  const cleanupAudio = () => {
    if (previewTimerRef.current) {
      clearInterval(previewTimerRef.current);
      previewTimerRef.current = null;
    }
    
    if (audioNodesRef.current.masterGain && audioContextRef.current) {
      const fadeOutTime = 0.3;
      audioNodesRef.current.masterGain.gain.linearRampToValueAtTime(0, audioContextRef.current.currentTime + fadeOutTime);
      
      setTimeout(() => {
        if (audioNodesRef.current.leftOsc) {
          audioNodesRef.current.leftOsc.stop();
        }
        if (audioNodesRef.current.rightOsc) {
          audioNodesRef.current.rightOsc.stop();
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }
        
        audioNodesRef.current = {};
        setIsPreviewing(false);
      }, fadeOutTime * 1000);
    } else {
      setIsPreviewing(false);
    }
  };
  
  const handlePreviewToggle = () => {
    generatePreview();
  };
  
  const updateChannelsFromBeat = (newBeat: number) => {
    setFrequency(newBeat);
    if (lockChannels) {
      setLeftFrequency(carrierFrequency);
      setRightFrequency(carrierFrequency + newBeat);
    }
  };
  
  const updateCarrier = (newCarrier: number) => {
    setCarrierFrequency(newCarrier);
    if (lockChannels) {
      setLeftFrequency(newCarrier);
      setRightFrequency(newCarrier + frequency);
    }
  };
  
  const updateLeftChannel = (newLeft: number) => {
    setLeftFrequency(newLeft);
    if (lockChannels) {
      setRightFrequency(newLeft + frequency);
    } else {
      setFrequency(Math.abs(rightFrequency - newLeft));
    }
  };
  
  const updateRightChannel = (newRight: number) => {
    setRightFrequency(newRight);
    if (lockChannels) {
      setLeftFrequency(newRight - frequency);
    } else {
      setFrequency(Math.abs(newRight - leftFrequency));
    }
  };
  
  const handleApplyCarrierPreset = (presetValue: number) => {
    updateCarrier(presetValue);
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (showAdvanced) {
      onCreateAdvancedSession?.([
        {
          id: '1',
          frequency: beatFrequency,
          duration: duration,
          leftWaveform,
          rightWaveform,
          volume: 100,
          color: categoryColor
        }
      ]);
    } else {
      onCreateSession(frequency, duration, `Custom ${beatFrequency}Hz Session`);
    }
  };
  
  return (
    <div className="glass rounded-2xl p-6 animate-fade-in">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">
          {showAdvanced ? "Advanced Session Designer" : "Basic Session Designer"}
        </h3>
        
        <div className="flex items-center space-x-2">
          <Switch
            id="advanced-mode"
            checked={showAdvanced}
            onCheckedChange={setShowAdvanced}
          />
          <label 
            htmlFor="advanced-mode" 
            className="text-sm font-medium cursor-pointer flex items-center gap-1"
          >
            <Settings className="h-4 w-4" />
            <span>Advanced</span>
          </label>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {!showAdvanced ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground mb-3">Basic mode for simple frequency selection, or toggle Advanced for detailed channel controls.</p>
            <FrequencySlider
              value={frequency}
              onChange={setFrequency}
            />
            <div className="flex items-center gap-2 mt-2">
              <Badge className={`${getBrainwaveCategoryColor(getBrainwaveCategory(frequency))} px-2 py-1`}>
                {getBrainwaveCategory(frequency)}
              </Badge>
              <span className="text-xs text-muted-foreground">{getFrequencyDescription(frequency)}</span>
            </div>
          </div>
        ) : (
          <div className="space-y-6 bg-muted/30 p-4 rounded-lg border border-muted">
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-sm font-medium">Advanced Beat Controls</h4>
              <button
                type="button"
                onClick={() => setLockChannels(!lockChannels)}
                className="p-1 rounded-md hover:bg-muted transition-colors"
                aria-label={lockChannels ? "Unlock channels" : "Lock channels"}
              >
                {lockChannels ? (
                  <Lock className="h-4 w-4" />
                ) : (
                  <LockOpen className="h-4 w-4" />
                )}
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="space-y-4">
                <FrequencySlider
                  label="Beat Frequency"
                  value={frequency}
                  onChange={updateChannelsFromBeat}
                  min={0.5}
                  max={40}
                  step={0.1}
                />
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`${categoryColor} px-2 py-1`}>{brainwaveCategory}</Badge>
                  <span className="text-xs text-muted-foreground">{getFrequencyDescription(beatFrequency)}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <FrequencySlider
                  label="Carrier Frequency"
                  value={carrierFrequency}
                  onChange={updateCarrier}
                  min={100}
                  max={500}
                  step={1}
                  description="Base frequency for binaural processing"
                  hideDescription={false}
                />
                
                <div className="grid grid-cols-2 gap-2 mt-2 sm:grid-cols-4">
                  <TooltipProvider>
                    {CARRIER_PRESETS.map(preset => (
                      <Tooltip key={preset.value}>
                        <TooltipTrigger asChild>
                          <Button 
                            type="button"
                            size="sm"
                            variant={carrierFrequency === preset.value ? "secondary" : "outline"}
                            onClick={() => handleApplyCarrierPreset(preset.value)}
                            className="h-auto py-2 text-xs w-full"
                          >
                            <div className="font-medium">{preset.displayLabel}</div>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{preset.label} - {preset.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </TooltipProvider>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="space-y-2">
                  <FrequencySlider
                    label="Left Channel"
                    value={leftFrequency}
                    onChange={updateLeftChannel}
                    min={100}
                    max={500}
                    step={0.1}
                    hideDescription={true}
                  />
                  
                  <div className="flex flex-wrap gap-1 mt-1">
                    <span className="text-xs text-muted-foreground mr-1">Waveform:</span>
                    {WAVEFORM_OPTIONS.map(wave => (
                      <Button
                        key={wave}
                        type="button"
                        size="sm"
                        variant={leftWaveform === wave ? "secondary" : "outline"}
                        onClick={() => setLeftWaveform(wave)}
                        className="h-6 px-2 text-xs"
                      >
                        <AudioLines className="h-3 w-3 mr-1" /> {wave}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <FrequencySlider
                    label="Right Channel"
                    value={rightFrequency}
                    onChange={updateRightChannel}
                    min={100}
                    max={500}
                    step={0.1}
                    hideDescription={true}
                  />
                  
                  <div className="flex flex-wrap gap-1 mt-1">
                    <span className="text-xs text-muted-foreground mr-1">Waveform:</span>
                    {WAVEFORM_OPTIONS.map(wave => (
                      <Button
                        key={wave}
                        type="button"
                        size="sm"
                        variant={rightWaveform === wave ? "secondary" : "outline"}
                        onClick={() => setRightWaveform(wave)}
                        className="h-6 px-2 text-xs"
                      >
                        <AudioLines className="h-3 w-3 mr-1" /> {wave}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="text-xs text-muted-foreground">
                <p className="mb-1">Current configuration:</p>
                <div className="font-mono bg-background/50 p-2 rounded">
                  Beat: {beatFrequency.toFixed(2)} Hz = |{rightFrequency.toFixed(2)} - {leftFrequency.toFixed(2)}| Hz
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Duration</span>
            <span className="text-sm font-mono">{duration} min</span>
          </div>
          
          <Slider
            value={[duration]}
            min={1}
            max={60}
            step={1}
            onValueChange={(values) => setDuration(values[0])}
          />
          
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>1 min</span>
            <span>60 min</span>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            type="button" 
            variant="outline"
            className="flex-1"
            onClick={handlePreviewToggle}
            disabled={isPreviewing && previewTimeLeft <= PREVIEW_FADEOUT_TIME}
          >
            {isPreviewing ? (
              <>
                <Pause className="h-4 w-4 mr-2" /> 
                Stop Preview ({previewTimeLeft}s)
              </>
            ) : (
              <>
                <Eye className="h-4 w-4 mr-2" /> 
                Preview ({PREVIEW_DURATION}s)
              </>
            )}
          </Button>
          
          <Button 
            type="submit" 
            className="flex-1"
            disabled={isPreviewing && previewTimeLeft <= PREVIEW_FADEOUT_TIME}
          >
            <Play className="h-4 w-4 mr-2" />
            Start {showAdvanced ? "Advanced" : "Basic"} Session
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CustomSession;
